
import { useState, useEffect } from 'react';
import { useResellerPlans } from '@/hooks/license/useResellerPlans';
import { createNewPlan } from '@/hooks/license/planOperations';
import type { ResellerPlanDetails } from '@/hooks/license/resellerPlans/types';

export interface AdminAccount {
  id: string;
  adminName: string;
  instanceName: string;
  domain: string;
  userCount: number;
  status: string;
  createdAt: Date;
}

export interface ActivePlan {
  id: string;
  name: string;
  status: string;
  admins: number;
  users: number;
}

export interface RevenueHistoryItem {
  month: string;
  totalRevenue: number;
  resellerAmount: number;
  masterLicenseAmount: number;
}

export interface ResellerPlanDataReturn {
  currentPlan: ResellerPlanDetails;
  currentMonth: string;
  setCurrentMonth: (month: string) => void;
  currentRevenueData: any; // Using any here as it's from mockResellerRevenues
  activePlans: ActivePlan[];
  adminAccounts: AdminAccount[];
  revenueHistory: RevenueHistoryItem[];
  isLoading: boolean;
}

export const useResellerPlanData = (): ResellerPlanDataReturn => {
  const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().substring(0, 7));
  
  // Get read-only reseller plan
  const { resellerPlan, isLoadingPlans } = useResellerPlans(true);
  
  // Use the returned plan or create a new one if none is returned
  const currentPlan = resellerPlan || createNewPlan();
  
  // Mock revenue data since mockResellerRevenues is missing
  const mockRevenueData = {
    monthYear: currentMonth,
    resellerId: 'reseller-1',
    totalRevenue: 1250,
    resellerAmount: 375,
    masterLicenseAmount: 875,
    splitPercentage: 30,
    activeAdminAccounts: 3,
    activeUserAccounts: 230
  };
  
  // Use mock data
  const currentRevenueData = mockRevenueData;

  // Generate mock revenue history data for the past 6 months
  const generateRevenueHistory = (): RevenueHistoryItem[] => {
    const history: RevenueHistoryItem[] = [];
    const today = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(today);
      date.setMonth(date.getMonth() - i);
      const monthYear = date.toISOString().substring(0, 7);
      
      const baseRevenue = 800 + Math.random() * 500;
      const growthFactor = 1 + (5 - i) * 0.1; // Simulate growth over time
      const totalRevenue = baseRevenue * growthFactor;
      const resellerPercentage = 30; // Default percentage
      const resellerAmount = totalRevenue * (resellerPercentage / 100);
      
      history.push({
        month: monthYear,
        totalRevenue: Math.round(totalRevenue),
        resellerAmount: Math.round(resellerAmount),
        masterLicenseAmount: Math.round(totalRevenue - resellerAmount)
      });
    }
    
    return history;
  };
  
  const revenueHistory = generateRevenueHistory();

  // Mock active plans data
  const activePlans = [
    {
      id: 'plan-1',
      name: 'Business Manager Pro',
      status: 'Active',
      admins: 2,
      users: 143
    },
    {
      id: 'plan-2',
      name: 'Referral Tracker',
      status: 'Active',
      admins: 1,
      users: 87
    }
  ];

  // Mock admin accounts data
  const adminAccounts = [
    {
      id: 'instance-1',
      adminName: 'John Admin',
      instanceName: 'Business Manager Pro',
      domain: 'business-pro.referme.site',
      userCount: 143,
      status: 'active',
      createdAt: new Date('2023-09-15')
    },
    {
      id: 'instance-2',
      adminName: 'Sarah Manager',
      instanceName: 'Referral Tracker',
      domain: 'refertracker.referme.site',
      userCount: 87,
      status: 'active',
      createdAt: new Date('2023-10-22')
    }
  ];

  return {
    currentPlan,
    currentMonth,
    setCurrentMonth,
    currentRevenueData,
    activePlans,
    adminAccounts,
    revenueHistory,
    isLoading: isLoadingPlans
  };
};
